import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Database, Download, FileArchive, FileText, ServerCog } from 'lucide-react';
import { Button, MenuButton, StatusPill } from '@/components/ui';
import { backupDownloadUrl } from '@/hooks/useBackups';
import { BACKUP_PROBLEM, backupSlotLabel } from '@/utils/backups';
import { formatBytes, formatDateTime, formatRelative } from '@/utils/format';
import { BACKUP_KIND_LABELS, BACKUP_VERIFICATION } from '@/utils/labels';
import type { BackupListItem, BackupSiteGroup } from '@/types/api';

const PARTS: Record<string, { label: string; icon: typeof FileArchive }> = {
  'files.tar': { label: 'Файлы', icon: FileArchive },
  'database.sql': { label: 'База', icon: Database },
  'manifest.json': { label: 'Манифест', icon: FileText },
};

/** One copy in its slot: when, what it holds, whether it was checked, and what can be done with it. */
function Slot({
  copy,
  mayDownload,
  mayRestore,
  onRestore,
}: {
  readonly copy: BackupListItem;
  readonly mayDownload: boolean;
  readonly mayRestore: boolean;
  readonly onRestore: (copy: BackupListItem) => void;
}): ReactNode {
  const state = BACKUP_VERIFICATION[copy.verification] ?? { label: copy.verification, tone: 'status-unknown' };
  const hosted = copy.siteEngine === 'external';
  return (
    <div className={`backup-slot backup-slot-${copy.verification}`}>
      <div className="row-between">
        <strong>{backupSlotLabel(copy)}</strong>
        <span className={`status ${state.tone}`}>{state.label}</span>
      </div>
      <span className="numeric" title={copy.createdAt}>
        {formatDateTime(copy.createdAt)}
      </span>
      <span className="text-sm muted">
        {formatRelative(copy.createdAt)} · {BACKUP_KIND_LABELS[copy.kind] ?? copy.kind}
      </span>
      <span className="text-sm muted">
        {copy.artefacts
          .filter((name) => name !== 'manifest.json')
          .map((name) => PARTS[name]?.label ?? name)
          .join(' + ')}{' '}
        · {formatBytes(copy.sizeBytes)} · {copy.storage === 's3' ? 'S3' : 'сервер панели'}
      </span>
      {copy.restoreTestedAt ? (
        <span className={`text-sm ${copy.restoreTestOk ? 'tone-healthy' : 'tone-down'}`}>{copy.restoreTestOk ? 'восстановление проверено' : 'восстановление не прошло'}</span>
      ) : null}
      {copy.verification === 'verified' && (mayDownload || mayRestore) ? (
        <div className="row-tight row-wrap backup-slot-actions">
          {mayDownload ? (
            <MenuButton
              label="Скачать"
              icon={Download}
              size="sm"
              align="start"
              items={copy.artefacts.map((name) => ({
                label: PARTS[name]?.label ?? name,
                icon: PARTS[name]?.icon ?? FileText,
                onSelect: () => window.location.assign(backupDownloadUrl(copy.id, name)),
              }))}
            />
          ) : null}
          {mayRestore ? (
            hosted ? (
              <Button size="sm" icon={ServerCog} onClick={() => onRestore(copy)}>
                Развернуть
              </Button>
            ) : (
              <Link to={`/sites/${copy.siteId}?tab=backups`} className="text-sm" title="Сайт панели восстанавливается со своей страницы">
                Восстановить на сайте
              </Link>
            )
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * A site's copies as numbered slots (ADR-0056): «Копия 1…N», an empty slot
 * where there is still room, then what stands outside the slots — a copy being
 * checked, a safety copy before a change, a failed one.
 */
export function SiteCopies({
  site,
  copiesPerSite,
  serverName,
  mayDownload,
  mayRestore,
  onRestore,
}: {
  readonly site: BackupSiteGroup;
  readonly copiesPerSite: number;
  readonly serverName: string | null;
  readonly mayDownload: boolean;
  readonly mayRestore: boolean;
  readonly onRestore: (copy: BackupListItem) => void;
}): ReactNode {
  const slotted = new Map(site.copies.filter((copy) => copy.slot !== null).map((copy) => [copy.slot!, copy]));
  const outside = site.copies.filter((copy) => copy.slot === null);
  const slots = Array.from({ length: Math.max(copiesPerSite, ...slotted.keys()) }, (_, index) => index + 1);

  return (
    <section className="card backup-site">
      <header className="card-head">
        <div className="stack stack-tight backup-site-name">
          <Link to={`/sites/${site.siteId}?tab=backups`} className="strong truncate">
            {site.siteName}
          </Link>
          <span className="text-sm muted truncate">{[site.siteDomain !== site.siteName ? site.siteDomain : null, serverName].filter(Boolean).join(' · ')}</span>
        </div>
        <div className="row-tight">
          {site.lastCopyAt ? <span className="text-sm muted nowrap">последняя {formatRelative(site.lastCopyAt)}</span> : null}
          <StatusPill map={BACKUP_PROBLEM} status={site.problem ?? 'ok'} />
        </div>
      </header>
      {site.problem === 'unprotected' && site.copies.length === 0 ? null : (
        <div className="backup-slots">
          {slots.map((slot) => {
            const copy = slotted.get(slot);
            return copy ? (
              <Slot key={copy.id} copy={copy} mayDownload={mayDownload} mayRestore={mayRestore} onRestore={onRestore} />
            ) : (
              <div key={`empty-${slot}`} className="backup-slot backup-slot-empty">
                <strong>Копия {slot}</strong>
                <span className="text-sm muted">свободна — займёт следующая копия</span>
              </div>
            );
          })}
          {outside.map((copy) => (
            <Slot key={copy.id} copy={copy} mayDownload={mayDownload} mayRestore={mayRestore} onRestore={onRestore} />
          ))}
        </div>
      )}
    </section>
  );
}
